import SnappPayIcon from "@assets/svg/snapp-pay.svg";
import SnappPayMiniIcon from "@assets/svg/snapp-pay-mini.svg";
import { commafy } from "@lib/index";
import { prevUrlBeforAuth } from "@store/reducers/auth";
import Alert from "assets/svg/student/alert-exit.svg";
import { Button } from "components";
import gate from "gate";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { convertMiladiToJalali, loadState, sortArrayByOrder } from "src/utils";

import { CampaignType } from "../../../enums/campaign-type";
import Modal from "../Modal";

type Props = {
  price: number;
  dicount: number;
  id: number;
  installmentPayments: any;
  isBuyable: boolean;
  badge?: string;
  allowSnapPay: boolean;
  snappPayInstallment: number;
  refPrerequisites: React.MutableRefObject<any>;
};

const ShoppingBox: FC<Props> = ({
  dicount,
  isBuyable,
  badge,
  refPrerequisites,
  price,
  installmentPayments,
  id,
  allowSnapPay,
  snappPayInstallment,
}) => {
  const [installments, setInstallments] = useState(installmentPayments);
  const { mutate: createUserActivityMutate } = useMutation(
    gate.createUserActivity
  );
  const state = useSelector((state: any) => state.auth);
  const [isSnapPayAvailable, setIsSnapPayAvailable] = useState<{
    text: string;
    icon: string;
  } | null>(null);
  const [isShowModal, setIsShowModal] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const query = useQueryClient();
  const [installment, setInstallment] = useState(false);
  const { mutate: checkValidForSnapp, isLoading: isLoadingForValidSnapp } =
    useMutation(gate.isValidForSnapPay, {
      onSuccess: (data: any) => {
        setIsSnapPayAvailable({
          text: data?.data?.response?.description,
          icon: data?.data?.logo,
        });
      },
      onError: () => {
        toast.error("متاسفانه عملیات با مشکل مواجه شد");
      },
    });

  const parseSnappPayInstallmentAmount = (
    description?: string
  ): number | null => {
    if (!description) return null;

    const persianDigits: Record<string, string> = {
      "۰": "0",
      "۱": "1",
      "۲": "2",
      "۳": "3",
      "۴": "4",
      "۵": "5",
      "۶": "6",
      "۷": "7",
      "۸": "8",
      "۹": "9",
    };

    const match = description.match(/ماهانه\s+([\d۰-۹,٬]+)\s*تومان/);
    if (!match || !match[1]) return null;

    const normalized = match[1]
      .split("")
      .map((ch) => (persianDigits[ch] !== undefined ? persianDigits[ch] : ch))
      .join("")
      .replace(/[٬,]/g, "");

    const value = Number(normalized);
    return Number.isNaN(value) ? null : value;
  };
  const { mutate, isLoading } = useMutation(gate.addOrder, {
    onSuccess: (data: any) => {
      state?.prevUrl &&
        dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
      if (!data.data.isSuccessful) {
        setIsShowModal(true);
      } else {
        query.invalidateQueries(["order-list"]);
        toast.success("این سفارش به سبد خرید شما اضافه شد");
      }
      let campaign: any = localStorage.getItem("campaign");
      if (campaign && JSON.parse(campaign)) {
        campaign = JSON.parse(campaign);
        createUserActivityMutate({
          campaignId: +campaign.campaign_id,
          followUpId: +campaign.followup_id,
          phoneNumber: campaign.phone_number,
          type: +CampaignType.AddToBasket,
          url: campaign.url,
          isPromotion: campaign?.isPromotion.toLowerCase() === "true",
        });
      }
    },
    onError: (data: any) => {
      state?.prevUrl &&
        dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
      if (!data.handleGlobally) {
        toast.error(data?.data?.message);
      }
    },
  });

  function checkIfHasPayedFalseInArray(array: any) {
    return array?.some((item: any) => item.hasPayed !== false);
  }

  const installmentPaymentsArr = installments?.filter(
    (item: any) => item?.hasPayed == false
  );
  useEffect(() => {
    if (allowSnapPay) {
      checkValidForSnapp(dicount ? dicount : price);
    }
  }, [allowSnapPay, price, dicount, checkValidForSnapp]);
  const handleAddOrder = () => {
    if (!Cookies.get("access_token")) {
      dispatch(prevUrlBeforAuth({ path: router.asPath, needRedirect: false }));
      router.push("/auth");
    } else {
      if (checkIfHasPayedFalseInArray(installments)) {
        router.push("/student/wallet");
      } else {
        if (installment) {
          mutate({
            price: sortArrayByOrder(installments, "order")[0]?.price ?? 0,
            productId: id,
            productType: 1,
            isInstallment: true,
            discount: 0,
          });
        } else {
          mutate({
            price: price ? price : 0,
            productId: id,
            isInstallment: false,
            productType: 1,
            discount: dicount ? dicount : 0,
          });
        }
      }
    }
  };

  useEffect(() => {
    const persistState = loadState();
    if (persistState?.auth?.prevUrl && persistState?.auth?.needRedirect) {
      handleAddOrder();
    } else {
      dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
    }
  }, [id]);

  return (
    <>
      <Modal
        visible={isShowModal}
        onClose={() => setIsShowModal(false)}
        className="h-screen w-screen"
      >
        <div className="relative mx-auto flex h-40 w-10/12 flex-col justify-between rounded bg-white py-6 px-4 md:h-[189px] md:w-[418px] md:p-[30px] xl:h-[189px]">
          <div className="flex gap-x-4">
            <Alert />

            <p className="self-center text-xs md:text-base">
              برای خرید این بسته ابتدا باید پیش نیاز را خریداری کنید.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-2 text-white">
            <Button
              onClick={() => setIsShowModal(false)}
              className="w-full rounded bg-[#B8B7B7] py-2 transition-all hover:ring-2"
            >
              انصراف
            </Button>
            <Button
              className="w-full rounded bg-[#EB7A33] py-2 text-white transition-all hover:ring-2"
              onClick={() => {
                refPrerequisites?.current?.scrollIntoView(true);
                setIsShowModal(false);
              }}
            >
              جزئیات پیش نیاز
            </Button>
          </div>
        </div>
      </Modal>
      <div className="mb-4 flex h-fit w-full flex-col justify-between bg-white p-5 shadow md:mb-0 md:min-h-[452px] lg:shadow-md xl:w-[34%]">
        <div>
          <div className="flex h-[52px] w-full gap-x-2 rounded-full bg-blue-550/10 p-[5px]">
            <button
              onClick={() => setInstallment(false)}
              className={`${
                !installment ? "text-white" : "text-blue-550"
              } w-full rounded-full text-sm transition-all lg:text-base`}
              style={{
                background: !installment
                  ? "linear-gradient(270deg, #366494 0%, #0480D4 50%, #27B2BC 100%)"
                  : "",
              }}
            >
              خرید نقدی
            </button>
            <button
              onClick={() => setInstallment(true)}
              className={`${
                installment ? "text-white" : "text-blue-550"
              } w-full rounded-full text-sm transition-all lg:text-base`}
              style={{
                background: installment
                  ? "linear-gradient(270deg, #366494 0%, #0480D4 50%, #27B2BC 100%)"
                  : "",
              }}
            >
              خرید اقساطی
            </button>
          </div>
          {!installment ? (
            isBuyable ? (
              <>
                {dicount && (
                  <div className="mt-8 text-sm text-[#F46A6A]">
                    قیمت اصلی دوره{" "}
                    <span className="line-through">{commafy(price)} تومان</span>
                  </div>
                )}{" "}
                <div className="mb-16 mt-3 text-sm">
                  قیمت خرید بسته{" "}
                  <span className="font-bold">
                    {dicount ? commafy(dicount) : commafy(price)}
                  </span>{" "}
                  تومان
                </div>
                {allowSnapPay ? (
                  <>
                    {!isLoadingForValidSnapp && isSnapPayAvailable ? (
                      <>
                        <hr className="mt-4" />
                        <div className="my-4 mx-auto flex w-fit items-center justify-center gap-2">
                          <div className="shrink-0">
                            <SnappPayIcon className="hidden w-[32px] md:inline-block lg:w-[40px]" />
                            <SnappPayMiniIcon className="inline-block w-[32px] md:hidden" />
                          </div>

                          <div>
                            <p className="text-[1a1c23] text-xs font-bold lg:text-sm">
                              هر قسط با اسنپ‌پی:
                              {(() => {
                                const amount = parseSnappPayInstallmentAmount(
                                  isSnapPayAvailable?.text
                                );
                                return amount
                                  ? ` ${commafy(amount)} تومان`
                                  : "";
                              })()}
                            </p>
                            <p className="mt-1 text-xs text-[#616475]">
                              چهارقسط ماهانه، بدون سود، چک و ضامن
                            </p>
                          </div>
                        </div>
                      </>
                    ) : undefined}
                  </>
                ) : undefined}
              </>
            ) : (
              <div className="pb-40 pt-5 text-sm">
                {badge}
              </div>
            )
          ) : isBuyable ? (
            <>
              {installmentPayments?.length !== 0 ? (
                <>
                  <div className="mt-7 mb-12 flex flex-col gap-y-[15px]">
                    <div>
                      مبلغ قسط قابل پرداخت
                      <span className="mx-2 font-bold">
                        {installments &&
                          commafy(installmentPaymentsArr[0]?.price)}
                        تومان
                      </span>
                    </div>
                    <div className="text-sm text-[#4F9B0B]">
                      اقساط در {installments?.length} قسط
                    </div>
                  </div>
                  <div
                    className="flex items-center justify-between rounded p-[10px]"
                    style={{
                      background:
                        "linear-gradient(270deg, rgba(54, 100, 148, 0.2) 0%, rgba(4, 128, 212, 0.2) 50%, rgba(39, 178, 188, 0.2) 100%)",
                    }}
                  >
                    <span>شرایط اقساط</span>
                    {/* <ArrowBottom fill="#4E4E56" /> */}
                  </div>
                  <ul className="flex w-full flex-col gap-y-5 py-7 text-gray-450">
                    {sortArrayByOrder(installments, "order")?.map(
                      (payment: any, index: number) => (
                        <li
                          className={`flex justify-between ${
                            payment.hasPayed && "line-through"
                          }`}
                          key={JSON.stringify(payment?.id)}
                        >
                          <span>قسط {index + 1}</span>
                          <span>{commafy(payment.price)} تومان</span>
                          <span>
                            {convertMiladiToJalali(payment?.validDate)}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                </>
              ) : (
                <div className="my-5 text-xs md:text-sm">
                  کاربر گرامی این کارگاه فقط به صورت نقدی قابل خرید است
                </div>
              )}
            </>
          ) : (
            <div className="pb-40 pt-5 text-sm">
              {badge}
            </div>
          )}
        </div>

        <Button
          disabled={
            !isBuyable || (installment && installmentPayments?.length == 0)
          }
          isLoading={isLoading}
          onClick={handleAddOrder}
          CClassName="h-[51px] lg:text-base text-sm w-full disabled:bg-gray-400 bg-blue-550 text-white"
        >
          {checkIfHasPayedFalseInArray(installments)
            ? "ادامه خرید اقساطی"
            : "افزودن به سبد خرید"}
        </Button>
      </div>
    </>
  );
};

export default ShoppingBox;
